import { Injectable } from '@angular/core';
import { Day } from '@app/utils/models/itinerary/day.model';
import { RouteDetails } from '@app/utils/models/itinerary/route-details.model';
import { Trip } from '@app/utils/models/itinerary/trip.model';
import { TravelState } from '@app/utils/models/store/travel-state.model';
import { StaticText } from '@app/utils/models/static-text.model';
import { StoreConfigService } from './store-config.service';
import { LoadingState } from '@app/utils/models/store/loading-state.model';
import { ItineraryState } from '@app/utils/models/store/itinerary-state.model';
import { ItineraryReducerService } from './reducers/itinerary-reducer.service';
import { ItinerarySelectorsService } from './selectors/itinerary-selectors.service';
import { Experience } from '@app/utils/models/itinerary/experience.model';
import { LOADING_MESSAGES } from '@app/utils/models/loading-messages';

@Injectable({
  providedIn: 'root',
})
export class StoreService extends StoreConfigService {
  constructor(
    itinerarySelectorService: ItinerarySelectorsService,
    private itineraryReducerService: ItineraryReducerService,
  ) {
    super(itinerarySelectorService);
  }

  public getState(): TravelState {
    return this.store$.getValue();
  }

  public getItineraryState(): ItineraryState {
    return this.getState().itineraryState;
  }

  cleanItineraryState() {
    this.setInitialItineraryState();
  }

  updateExperience(experienceId: number, newExperience: Experience) {
    const oldState = this.getItineraryState();
    const newState = this.itineraryReducerService.updateExperience(oldState, experienceId, newExperience);
    this.setItineraryState(newState);
  }

  setRouteDetails(routeDetails: RouteDetails) {
    this.setItineraryState({ routeDetails });
  }

  setTrip(trip: Trip) {
    this.setItineraryState({
      trip,
      isSampleTrip: trip.isSample,
      isAmex: trip.isAmex,
    });
  }

  setQuote(quote: string) {
    const itineraryState = this.getState().itineraryState;
    if (itineraryState.trip) {
      const trip: Trip = itineraryState.trip;
      trip.totalPrice = quote;
      this.setItineraryState({ ...itineraryState, trip });
    }
  }

  setStaticTexts(staticTexts: StaticText) {
    this.setItineraryState({ staticTexts });
  }

  setActiveDay(activeDay: number) {
    const numberOfDays = this.getState().itineraryState.trip?.numberOfDays;
    if (numberOfDays && activeDay <= numberOfDays) {
      this.setItineraryState({ activeDay });
    }
  }
  setDays(days: Day[]) {
    this.setItineraryState({ days });
  }

  setIsLoading(isLoading: boolean, messageKey: string = LOADING_MESSAGES.default) {
    this.setLoadingState({
      isLoading,
      messageKey: messageKey,
    });
  }

  setLoadingScreenColor(screenColor: 'white' | 'black') {
    this.setLoadingState({ screenColor });
  }

  setStateLoading(loadingState: LoadingState) {
    const currentState = this.store$.getValue();
    this.store$.next({ ...currentState, loadingState });
  }
}
